import { useCallback, useEffect, useRef, useState } from 'react'
import { api, saveEditedImage } from '../api'
import type { CaptureOptions } from '../api'
import type { ActiveExam } from './Shell'
import { MediaImage } from '../components/MediaImage'
import { MediaViewer } from '../components/MediaViewer'
import { ImageEditor } from '../components/ImageEditor'
import { IniciarExameModal } from './IniciarExame'

interface Shot {
  id: string
  sha: string
  kind: 'photo' | 'video'
  mime: string
  time: string
  marked: boolean
  durationMs?: number
  position: number
  caption?: string
}

function clock(sec: number) {
  const m = String(Math.floor(sec / 60)).padStart(2, '0')
  const s = String(sec % 60).padStart(2, '0')
  return `${m}:${s}`
}

function nowTime(): string {
  const d = new Date()
  const p = (x: number) => String(x).padStart(2, '0')
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

function pickVideoMime(): string {
  const candidates = [
    'video/mp4;codecs=h264',
    'video/mp4',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ]
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c
  }
  return ''
}

// Identifica esta tela para o motor saber de quem é cada vídeo ao vivo.
function newViewerId(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

// Imagem vazia de 1 pixel: trocar o src por ela encerra o stream anterior.
// Tecla de captura deste computador (o pedal é um teclado de uma tecla só).
const KEY_STORE = 'cscan.captureKey'
function savedKey(): string {
  try { return localStorage.getItem(KEY_STORE) || 'Space' } catch { return 'Space' }
}
function keyLabel(code: string): string {
  if (code === 'Space') return 'ESPAÇO'
  return code.replace(/^Key|^Digit/, '').replace(/^Numpad/, 'NUM ').replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase()
}

const BLANK_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='

// Tela de captura: usa a placa UVC (a torre de endoscopia aparece como
// webcam). Foto = frame do vídeo em JPEG; gravação = MediaRecorder.
// Cada captura é gravada no storage local e VINCULADA ao exame — é esse
// vínculo que alimenta o laudo.
export function Captura({ exam, onExamStarted, onOpenReport }: {
  exam: ActiveExam | null
  onExamStarted: (examId: string, patientId: string, patientName: string) => void
  onOpenReport: (reportId: string) => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const previewEl = useRef<HTMLImageElement | null>(null)
  const viewerRef = useRef('')
  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const recStartRef = useRef<number>(0)

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [deviceId, setDeviceId] = useState('')
  const [ready, setReady] = useState(false)
  const [error, setError] = useState('')
  const [shots, setShots] = useState<Shot[]>([])
  const [activeShot, setActiveShot] = useState('')
  const [recording, setRecording] = useState(false)
  const [recSec, setRecSec] = useState(0)
  const [busyMsg, setBusyMsg] = useState('')
  const [signal, setSignal] = useState('')
  const [seenDevices, setSeenDevices] = useState<string[]>([])
  const [mode, setMode] = useState<'native' | 'browser' | ''>('')
  const [nativeDevices, setNativeDevices] = useState<{ id: string; name: string }[]>([])
  const [nativeDevice, setNativeDevice] = useState('')
  const [previewSrc, setPreviewSrc] = useState('')
  const [openingReport, setOpeningReport] = useState(false)
  const [showStart, setShowStart] = useState(false)
  const [opts, setOpts] = useState<CaptureOptions | null>(null)
  const [viewing, setViewing] = useState<number | null>(null)
  const [editing, setEditing] = useState<number | null>(null)
  const [captureKey, setCaptureKey] = useState(savedKey)
  const [listening, setListening] = useState(false)

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    setReady(false)
  }, [])

  const start = useCallback(async (id?: string) => {
    setError('')
    // Navegador só libera câmera em conexão segura (HTTPS ou localhost).
    // É o caso típico de quem testa pelo link http:// do servidor.
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      setError('O navegador só libera a câmera em conexão segura (https://). Abra o endereço HTTPS do servidor CScan.')
      return
    }
    stopStream()
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: id ? { deviceId: { exact: id } } : { width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play().catch(() => undefined)
      }
      setReady(true)
      // sinal real do dispositivo (resolução e taxa) — validação do equipamento
      const st = stream.getVideoTracks()[0]?.getSettings()
      if (st?.width && st?.height) {
        setSignal(`${st.width}×${st.height}${st.frameRate ? ` · ${Math.round(st.frameRate)} fps` : ''}`)
      } else {
        setSignal('')
      }
      const list = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'videoinput')
      setDevices(list)
      if (!id) {
        const track = stream.getVideoTracks()[0]
        const settings = track?.getSettings()
        if (settings?.deviceId) setDeviceId(settings.deviceId)
      }
    } catch (err) {
      // ajuda o diagnóstico: mostra o que o sistema operacional enxerga
      try {
        const list = (await navigator.mediaDevices.enumerateDevices())
          .filter((d) => d.kind === 'videoinput')
          .map((d, i) => d.label || `Dispositivo de vídeo ${i + 1}`)
        setSeenDevices(list)
      } catch {
        setSeenDevices([])
      }
      setError(
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'Permissão de câmera negada. Autorize o acesso nas configurações do sistema.'
          : 'Nenhum dispositivo de captura encontrado. Verifique o cabo USB e a saída da torre.',
      )
    }
  }, [stopStream])

  useEffect(() => {
    let alive = true
    void api.capture.devices()
      .then((out) => {
        if (!alive) return
        setNativeDevices(out.items)
        if (out.available && out.items.length > 0) {
          setMode('native')
          void selectNative(out.items[0].id)
          return
        }
        setMode('browser')
      })
      .catch(() => { if (alive) setMode('browser') })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (mode !== 'browser') return
    void start()
    return stopStream
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  // O vídeo ao vivo do aparelho é um stream sem fim num <img>. Tirar o
  // elemento da tela não faz o navegador encerrar a conexão — e é ela que
  // mantém a câmera ligada. Ao sair da tela, o motor é avisado e o stream é
  // cortado dos dois lados.
  const closePreview = useCallback(() => {
    api.capture.closePreview(viewerRef.current)
    viewerRef.current = ''
    if (previewEl.current) previewEl.current.src = BLANK_IMAGE
  }, [])
  useEffect(() => closePreview, [closePreview])

  async function selectNative(dev: string) {
    setNativeDevice(dev)
    setError('')
    setReady(false)
    try {
      const { url } = await api.capture.previewUrl(dev)
      closePreview() // o vídeo do aparelho anterior não fica aberto
      viewerRef.current = newViewerId()
      setPreviewSrc(`${url}&v=${viewerRef.current}`)
      setReady(true)
    } catch {
      setError('Não foi possível abrir este dispositivo de captura.')
    }
    api.capture.options(dev).then(setOpts).catch(() => setOpts(null))
  }

  // Trocar a entrada ou a resolução reabre o aparelho com a nova escolha.
  async function changeOption(patch: { input?: string; quality?: string }) {
    if (!nativeDevice || recording) return
    setError('')
    try {
      await api.capture.setOptions(nativeDevice, patch)
      await selectNative(nativeDevice)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'falha ao trocar a opção do aparelho')
    }
  }

  // A janela de ajustes é do próprio driver; o aparelho precisa estar livre
  // enquanto ela está aberta.
  async function configureDevice() {
    if (!nativeDevice || recording || busyMsg) return
    setError('')
    setBusyMsg('Ajustes do aparelho abertos…')
    closePreview()
    setReady(false)
    try {
      await api.capture.configure(nativeDevice)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'falha ao abrir os ajustes do aparelho')
    } finally {
      setBusyMsg('')
      await selectNative(nativeDevice)
    }
  }

  // Carrega as capturas já vinculadas ao exame (a sessão é do exame, não da tela)
  const loadShots = useCallback(async (examId: string) => {
    const out = await api.exams.get(examId)
    setShots(out.media.map((m) => ({
      id: m.link.id,
      sha: m.media.sha256,
      kind: m.link.kind === 'video' ? 'video' : 'photo',
      mime: m.media.mime,
      time: (m.link.createdAt ?? '').slice(11, 19) || nowTime(),
      marked: m.link.inReport !== false,
      durationMs: m.media.durationMs,
      position: m.link.position,
      caption: m.link.caption,
    })))
  }, [])
  useEffect(() => {
    if (!exam) { setShots([]); setHasReport(false); return }
    void api.reports.list({ examId: exam.id })
      .then((out) => setHasReport(out.total > 0))
      .catch(() => setHasReport(false))
    void loadShots(exam.id).catch(() => undefined)
  }, [exam, loadShots])

  // A edição grava uma cópia: a foto original continua no exame, desmarcada.
  async function saveEdited(index: number, blob: Blob, width: number, height: number) {
    const s = shots[index]
    if (!s || !exam) return
    await saveEditedImage(exam.id, { linkId: s.id, position: s.position, caption: s.caption }, blob, width, height)
    await loadShots(exam.id)
    setEditing(null)
    setViewing(null)
  }

  useEffect(() => {
    if (!recording) return
    const t = setInterval(() => setRecSec((s) => s + 1), 1000)
    return () => clearInterval(t)
  }, [recording])

  const addShot = useCallback(async (media: { id: string; sha256: string; mime: string; durationMs?: number }, kind: 'photo' | 'video') => {
    let linkId = media.id
    let position = 0
    if (exam) {
      try {
        const link = await api.exams.linkMedia(exam.id, { mediaId: media.id, kind })
        linkId = link.id
        position = link.position
      } catch {
        setError('Captura salva, mas falhou o vínculo com o exame.')
      }
    }
    setShots((list) => [...list, {
      id: linkId, sha: media.sha256, kind, mime: media.mime,
      time: nowTime(), marked: true, durationMs: media.durationMs, position,
    }])
  }, [exam])

  const capture = useCallback(async () => {
    if (!exam) { setShowStart(true); return }

    // Caminho nativo (aparelhos com driver próprio): o agente já mantém o
    // quadro atual do exame; basta salvá-lo.
    if (mode === 'native') {
      if (busyMsg || !nativeDevice) return
      setBusyMsg('Salvando…')
      try {
        const media = await api.capture.photo(nativeDevice, 1280, 720)
        await addShot(media, 'photo')
      } catch (err) {
        setError(err instanceof Error ? err.message : 'falha ao capturar')
      } finally {
        setBusyMsg('')
      }
      return
    }

    const video = videoRef.current
    if (!video || !video.videoWidth || busyMsg) return
    setBusyMsg('Salvando…')
    try {
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      canvas.getContext('2d')!.drawImage(video, 0, 0)
      const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.94))
      if (!blob) throw new Error('falha ao gerar imagem')
      const media = await api.media.upload(blob, { mime: 'image/jpeg', width: canvas.width, height: canvas.height })
      await addShot(media, 'photo')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'falha ao capturar')
    } finally {
      setBusyMsg('')
    }
  }, [addShot, busyMsg, exam, mode, nativeDevice])

  const toggleRec = useCallback(async () => {
    if (!exam) { setShowStart(true); return }

    if (mode === 'native') {
      if (!nativeDevice) return
      try {
        if (!recording) {
          await api.capture.recordStart(nativeDevice, 1280, 720)
          setRecSec(0)
          setRecording(true)
        } else {
          const media = await api.capture.recordStop()
          setRecording(false)
          await addShot(media, 'video')
        }
      } catch (err) {
        setRecording(false)
        setError(err instanceof Error ? err.message : 'falha na gravação')
      }
      return
    }

    const stream = streamRef.current
    if (!stream) return
    if (!recording) {
      const mime = pickVideoMime()
      try {
        const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
        chunksRef.current = []
        rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data) }
        rec.onstop = async () => {
          const dur = Date.now() - recStartRef.current
          const blob = new Blob(chunksRef.current, { type: mime || 'video/webm' })
          setBusyMsg('Salvando vídeo…')
          try {
            const media = await api.media.upload(blob, { mime: blob.type, durationMs: dur })
            await addShot(media, 'video')
          } catch (err) {
            setError(err instanceof Error ? err.message : 'falha ao salvar vídeo')
          } finally {
            setBusyMsg('')
          }
        }
        recStartRef.current = Date.now()
        rec.start(1000)
        recorderRef.current = rec
        setRecSec(0)
        setRecording(true)
      } catch {
        setError('Gravação não suportada neste ambiente')
      }
    } else {
      recorderRef.current?.stop()
      recorderRef.current = null
      setRecording(false)
    }
  }, [recording, addShot, exam, mode, nativeDevice])

  // A imagem marcada entra no laudo; a escolha fica gravada no exame.
  async function toggleMarked(shot: Shot) {
    const marked = !shot.marked
    setShots((list) => list.map((x) => (x.id === shot.id ? { ...x, marked } : x)))
    if (!exam) return
    try {
      await api.exams.updateLink(shot.id, { inReport: marked })
    } catch {
      setShots((list) => list.map((x) => (x.id === shot.id ? { ...x, marked: !marked } : x)))
      setError('falha ao marcar a imagem')
    }
  }

  // Tecla de captura (barra de espaço ou a tecla que o pedal envia)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (listening) {
        e.preventDefault()
        setListening(false)
        if (e.code !== 'Escape') {
          setCaptureKey(e.code)
          try { localStorage.setItem(KEY_STORE, e.code) } catch { /* vale só nesta sessão */ }
        }
        return
      }
      if (viewing !== null) return
      const target = e.target as HTMLElement
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return
      if (e.code === captureKey) {
        e.preventDefault()
        void capture()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [capture, captureKey, listening, viewing])

  async function openOrCreateReport() {
    if (!exam) { setShowStart(true); return }
    setOpeningReport(true)
    setError('')
    try {
      const existing = (await api.reports.list({ examId: exam.id })).items[0]
      if (existing) {
        onOpenReport(existing.id)
      } else {
        const created = await api.reports.create(exam.id)
        onOpenReport(created.report.id)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'falha ao abrir laudo')
    } finally {
      setOpeningReport(false)
    }
  }

  const marked = shots.filter((s) => s.marked).length
  const [hasReport, setHasReport] = useState(false)

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* preview */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: 'var(--media-2)' }}>
          <div style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14 }}>
            <div className="media-surface" style={{ width: '100%', maxWidth: 1180, aspectRatio: '16/9', borderRadius: 6, position: 'relative', overflow: 'hidden', border: '1px solid #202b31' }}>
              {mode === 'native' ? (
                previewSrc && (
                  <img ref={(el) => { if (el) previewEl.current = el }} src={previewSrc} alt="captura do exame"
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: ready ? 'block' : 'none' }} />
                )
              ) : (
                <video ref={videoRef} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'contain', display: ready ? 'block' : 'none' }} />
              )}

              {mode === 'browser' && !ready && !error && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4a5b66', font: '400 11px/1.4 var(--font-mono)', letterSpacing: '.14em', textTransform: 'uppercase' }}>
                  inicializando captura…
                </div>
              )}
              {error && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: 'rgba(0,0,0,.72)', padding: 24, textAlign: 'center' }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', border: '1.5px solid var(--danger)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--danger)', font: '600 18px/1 var(--font-sans)' }}>!</div>
                  <div style={{ font: '400 12px/1.5 var(--font-mono)', color: '#8fa2ad', maxWidth: 460 }}>{error}</div>
                  {seenDevices.length > 0 && (
                    <div style={{ padding: '8px 12px', border: '1px solid rgba(255,255,255,.12)', borderRadius: 6, background: 'rgba(0,0,0,.35)', font: '400 11px/1.6 var(--font-mono)', color: '#8fa2ad', textAlign: 'left' }}>
                      O sistema enxerga {seenDevices.length} câmera(s):<br />
                      {seenDevices.map((d) => <span key={d}>· {d}<br /></span>)}
                    </div>
                  )}
                  {seenDevices.length === 0 && (
                    <div style={{ font: '400 11px/1.6 var(--font-mono)', color: '#6b7d88', maxWidth: 420 }}>
                      O sistema não reconheceu nenhuma câmera. Se o adaptador não aparecer nem no
                      aplicativo de câmera do sistema, ele não é compatível como câmera padrão (UVC).
                    </div>
                  )}
                  <button className="btn" onClick={() => void start(deviceId || undefined)} style={{ background: 'transparent', color: '#e6eef3', borderColor: 'rgba(255,255,255,.18)' }}>
                    Tentar reconectar
                  </button>
                </div>
              )}

              {recording && (
                <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', alignItems: 'center', gap: 7, padding: '5px 10px', borderRadius: 5, background: 'rgba(168,50,38,.9)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', animation: 'rec 1.1s infinite' }} />
                  <span style={{ font: '600 11.5px/1 var(--font-mono)', color: '#fff' }}>REC {clock(recSec)}</span>
                </div>
              )}
              {busyMsg && (
                <div style={{ position: 'absolute', bottom: 12, right: 12, padding: '5px 10px', borderRadius: 5, background: 'rgba(224,150,31,.92)', font: '600 11px/1 var(--font-mono)', color: '#211602' }}>
                  {busyMsg}
                </div>
              )}
              {ready && (
                <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', alignItems: 'center', gap: 7, padding: '5px 9px', borderRadius: 5, background: 'rgba(0,0,0,.55)', border: '1px solid rgba(255,255,255,.1)' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ok)' }} />
                  <span style={{ font: '500 11px/1 var(--font-mono)', color: '#cfdae1', letterSpacing: '.04em' }}>
                    {mode === 'native' ? 'captura do equipamento' : signal || 'sinal detectado'}
                  </span>
                </div>
              )}
              <div style={{ position: 'absolute', bottom: 12, left: 12, font: '400 10.5px/1 var(--font-mono)', color: '#5d6f79' }}>
                {exam ? `${exam.patientName} · exame em andamento` : ''}
              </div>
              {!exam && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, background: 'rgba(8,12,15,.82)' }}>
                  <div style={{ font: '600 15px/1.3 var(--font-sans)', color: '#e6eef3' }}>Inicie um exame para capturar</div>
                  <div style={{ font: '400 11.5px/1.6 var(--font-mono)', color: '#8fa2ad', maxWidth: 380, textAlign: 'center' }}>
                    As imagens ficam vinculadas ao paciente e alimentam o laudo.
                  </div>
                  <button className="btn btn-primary" onClick={() => setShowStart(true)} style={{ height: 40, padding: '0 22px', fontSize: 14 }}>
                    Iniciar exame
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* controles */}
          <div style={{ flex: 'none', background: 'var(--surface)', borderTop: '1px solid var(--border)', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
            <button className="btn" onClick={() => void toggleRec()} style={{ height: 40, padding: '0 16px', display: 'flex', alignItems: 'center', gap: 9, fontWeight: 600, fontSize: 13 }}>
              <span style={{ width: 11, height: 11, borderRadius: recording ? 2 : '50%', background: 'var(--danger)' }} />
              {recording ? 'Parar gravação' : 'Gravar vídeo'}
            </button>

            <button className="btn btn-primary" onClick={() => void capture()} disabled={!ready || !!busyMsg} style={{ height: 52, padding: '0 30px', borderRadius: 9, fontSize: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              Capturar foto
              <span style={{ padding: '3px 8px', borderRadius: 4, background: 'rgba(255,255,255,.16)', font: '600 10.5px/1 var(--font-mono)', letterSpacing: '.08em' }}>{keyLabel(captureKey)}</span>
            </button>

            <button className="btn" onClick={(e) => { e.currentTarget.blur(); setListening((v) => !v) }} style={{ height: 40, padding: '0 14px', fontSize: 12.5 }}>
              {listening ? 'Pressione a tecla ou o pedal…' : 'Tecla do pedal'}
            </button>
          </div>

          {/* faixa do exame */}
          <div style={{ flex: 'none', background: 'var(--surface)', borderTop: '1px solid var(--border)', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {exam ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--primary-soft)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', font: '600 12px/1 var(--font-sans)' }}>
                  {exam.patientName.split(' ').map((p) => p[0]).slice(0, 2).join('')}
                </div>
                <div>
                  <div style={{ font: '600 13.5px/1.2 var(--font-sans)' }}>{exam.patientName}</div>
                  <div style={{ font: '400 11px/1.3 var(--font-mono)', color: 'var(--text-2)' }}>Exame em andamento</div>
                </div>
                <div style={{ flex: 1 }} />
                <button className="btn" onClick={() => setShowStart(true)} style={{ height: 30 }}>
                  Trocar exame
                </button>
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', border: '1.5px dashed var(--border-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)' }}>?</div>
                <div>
                  <div style={{ font: '600 13.5px/1.2 var(--font-sans)', color: 'var(--text-2)' }}>Nenhum exame em andamento</div>
                  <div style={{ font: '400 11px/1.3 var(--font-mono)', color: 'var(--text-3)' }}>
                    Comece um exame para vincular as capturas e gerar o laudo.
                  </div>
                </div>
              </div>
            )}
            {!exam ? (
              <button className="btn btn-primary" onClick={() => setShowStart(true)} style={{ height: 34, padding: '0 18px' }}>
                Iniciar exame
              </button>
            ) : hasReport ? (
              <button className="btn btn-primary" onClick={() => void openOrCreateReport()} style={{ height: 34, padding: '0 18px' }}>
                Abrir laudo
              </button>
            ) : (
              <button className="btn btn-primary" onClick={() => void openOrCreateReport()} disabled={openingReport} style={{ height: 34, padding: '0 18px' }}>
                {openingReport ? 'Criando…' : <>Criar laudo <span style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(255,255,255,.16)', font: '600 10px/1 var(--font-mono)' }}>{marked} imgs</span></>}
              </button>
            )}
          </div>
        </div>

        {/* galeria lateral */}
        <div style={{ width: 218, flex: 'none', background: 'var(--surface)', borderLeft: '1px solid var(--border)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ flex: 'none', padding: '9px 11px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ font: '600 11.5px/1 var(--font-sans)', letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-2)' }}>Imagens: {shots.length}</span>
            <span style={{ font: '400 10.5px/1 var(--font-mono)', color: 'var(--accent)' }}>{marked} marcadas</span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 9, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0, background: 'var(--media-2)' }}>
            {shots.map((s) => (
              <div key={s.id} className="thumb" data-active={activeShot === s.id ? '1' : '0'}
                onClick={() => { setActiveShot(s.id); setViewing(shots.findIndex((x) => x.id === s.id)) }}>
                {s.kind === 'photo' ? (
                  <MediaImage sha={s.sha} alt={`captura ${s.time}`} style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <div className="media-surface" style={{ width: '100%', aspectRatio: '4/3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cfdae1', font: '500 11px/1 var(--font-mono)' }}>
                    ▶ {clock(Math.round((s.durationMs ?? 0) / 1000))}
                  </div>
                )}
                <div style={{ position: 'absolute', top: 0, left: 0, padding: '2px 5px', background: 'rgba(0,0,0,.6)', font: '600 10px/1.3 var(--font-mono)', color: '#cfdae1', borderBottomRightRadius: 4 }}>
                  {s.time}
                </div>
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', gap: 4, padding: '3px 4px', background: 'rgba(0,0,0,.62)' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); void toggleMarked(s) }}
                    style={{ width: 16, height: 16, flex: 'none', border: '1px solid rgba(255,255,255,.35)', borderRadius: 3, background: s.marked ? 'var(--accent-line)' : 'transparent', color: '#0b1013', font: '700 10px/1 var(--font-sans)', cursor: 'pointer' }}
                  >
                    {s.marked ? '✓' : ''}
                  </button>
                  <span style={{ flex: 1, font: '400 9.5px/1 var(--font-mono)', color: '#93a7b2' }}>{s.kind === 'video' ? 'vídeo' : 'foto'}</span>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation()
                      if (!exam) { setShots((list) => list.filter((x) => x.id !== s.id)); return }
                      try {
                        await api.exams.unlink(s.id)
                        setShots((list) => list.filter((x) => x.id !== s.id))
                      } catch { setError('falha ao remover captura') }
                    }}
                    style={{ width: 16, height: 16, flex: 'none', border: 'none', borderRadius: 3, background: 'transparent', color: '#e2705f', font: '400 12px/1 var(--font-sans)', cursor: 'pointer' }}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
            {shots.length === 0 && (
              <div style={{ border: '1px dashed #2b3841', borderRadius: 6, padding: '18px 12px', textAlign: 'center', font: '400 10.5px/1.5 var(--font-mono)', color: '#5d6f79' }}>
                Nenhuma imagem.<br />Pressione {keyLabel(captureKey)} para capturar.
              </div>
            )}
          </div>
          <div style={{ flex: 'none', padding: '8px 11px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div className="field">
              <span>Dispositivo</span>
              {mode === 'native' ? (
                <select className="input" value={nativeDevice} onChange={(e) => void selectNative(e.target.value)} style={{ width: '100%' }}>
                  {nativeDevices.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              ) : (
                <select className="input" value={deviceId} onChange={(e) => { setDeviceId(e.target.value); void start(e.target.value) }} style={{ width: '100%' }}>
                  {devices.length === 0 && <option value="">—</option>}
                  {devices.map((d, i) => (
                    <option key={d.deviceId} value={d.deviceId}>{d.label || `Câmera ${i + 1}`}</option>
                  ))}
                </select>
              )}
            </div>
            {mode === 'native' && opts && opts.inputs.length > 1 && (
              <div className="field">
                <span>Entrada</span>
                <select className="input" value={opts.input} disabled={recording} onChange={(e) => void changeOption({ input: e.target.value })} style={{ width: '100%' }}>
                  {opts.inputs.map((i) => <option key={i.pin} value={i.pin}>{i.name}</option>)}
                </select>
              </div>
            )}
            {mode === 'native' && opts?.has4k && (
              <div className="field">
                <span>Resolução</span>
                <select className="input" value={opts.quality} disabled={recording} onChange={(e) => void changeOption({ quality: e.target.value })} style={{ width: '100%' }}>
                  <option value="fullhd">Full HD</option>
                  <option value="4k">Até 4K</option>
                </select>
              </div>
            )}
            {mode === 'native' && opts?.configurable && (
              <button className="btn" onClick={() => void configureDevice()} disabled={recording || !!busyMsg} style={{ height: 28, fontSize: 11.5 }}>
                Ajustes do aparelho
              </button>
            )}
          </div>
        </div>
      </div>

      {viewing !== null && shots[viewing] && (
        <MediaViewer
          items={shots.map((x) => ({ sha: x.sha, mime: x.mime }))}
          index={viewing}
          onIndex={setViewing}
          onClose={() => setViewing(null)}
          onEdit={exam ? setEditing : undefined}
        />
      )}
      {editing !== null && shots[editing] && (
        <ImageEditor
          sha={shots[editing].sha}
          onCancel={() => setEditing(null)}
          onSave={(blob, w, h) => saveEdited(editing, blob, w, h)}
        />
      )}

      {showStart && (
        <IniciarExameModal
          onClose={() => setShowStart(false)}
          onStarted={(examId, patientId, patientName) => {
            setShowStart(false)
            onExamStarted(examId, patientId, patientName)
          }}
        />
      )}
    </div>
  )
}
